import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store/login-session.store');

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export interface LoginSessionState {
  email: string | null;
  session: string | null;
  inviteToken: string | null;
  redirectUrl: string | null;
  isConfirmationFlow: boolean;
  
  // Actions
  setLoginSession: (email: string, session: string, redirectUrl?: string) => void;
  setRegistrationSession: (email: string, session: string, inviteToken?: string, redirectUrl?: string) => void;
  clearSession: () => void;
}

const initialState = {
  email: null, 
  session: null, 
  inviteToken: null, 
  redirectUrl: null,
  isConfirmationFlow: false
};

// Custom storage with 1 hour expiration
const createExpiringStorage = (expireInMs = 3600000) => { // 1 hour default
  return createJSONStorage(() => ({
    getItem: (name) => {
      try {
        const value = localStorage.getItem(name);
        if (!value) return null;
        
        const data = JSON.parse(value);
        const timestamp = data.timestamp || 0;
        
        // If stored more than specified time ago, clear it
        if (Date.now() - timestamp > expireInMs) {
          localStorage.removeItem(name);
          return null;
        }
        
        return data.state;
      } catch (e) {
        return null;
      }
    },
    setItem: (name, value) => {
      try {
        const valueWithTimestamp = JSON.stringify({
          state: value,
          timestamp: Date.now(),
        });
        localStorage.setItem(name, valueWithTimestamp);
      } catch (e) {
        logger.error('Error storing login session:', e);
      }
    },
    removeItem: (name) => localStorage.removeItem(name),
  }));
};

export const useLoginSession = create<LoginSessionState>()(
  persist(
    (set) => ({
      ...initialState,
      
      setLoginSession: (email: string, session: string, redirectUrl?: string) => 
        set({
          email: normalizeEmail(email),
          session,
          inviteToken: null,
          redirectUrl: redirectUrl || null,
          isConfirmationFlow: true,
        }),
      
      setRegistrationSession: (email: string, session: string, inviteToken?: string, redirectUrl?: string) => 
        set({
          email: normalizeEmail(email),
          session,
          inviteToken: inviteToken || null,
          redirectUrl: redirectUrl || null,
          isConfirmationFlow: true,
        }),
      
      clearSession: () => set(initialState),
    }),
    {
      name: 'scrambl-login-session',
      storage: createExpiringStorage(3600000), // 1 hour expiration
    }
  )
); 
